import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeftRight, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { converterCategories, unitsByCategory } from '../data/mock';
import { convertUnit, formatNumber } from '../utils/conversion';

// Map converter IDs to their translation key prefixes
const getUnitTranslationKey = (converterId) => {
  const keyMap = {
    'length': 'lengthUnits',
    'area': 'areaUnits',
    'weight': 'weightUnits',
    'volume': 'volumeUnits',
    'temperature': 'temperatureUnits',
    'speed': 'speedUnits',
    'time': 'timeUnits',
    'pressure': 'pressureUnits',
    'energy': 'energyUnits',
    'power': 'powerUnits',
    'angle': 'angleUnits',
    'force': 'forceUnits',
    'data-storage': 'dataStorageUnits',
    'frequency': 'frequencyUnits'
  };
  return keyMap[converterId] || `${converterId}Units`;
};

// Extended units data for full converters
const extendedUnits = {
  pressure: [
    { id: 'pascal', name: 'Pascal', symbol: 'Pa', factor: 1 },
    { id: 'kilopascal', name: 'Kilopascal', symbol: 'kPa', factor: 1000 },
    { id: 'bar', name: 'Bar', symbol: 'bar', factor: 100000 },
    { id: 'psi', name: 'PSI', symbol: 'psi', factor: 6894.757 },
    { id: 'atm', name: 'Atmosphere', symbol: 'atm', factor: 101325 },
    { id: 'torr', name: 'Torr', symbol: 'Torr', factor: 133.3224 },
    { id: 'mmhg', name: 'mmHg', symbol: 'mmHg', factor: 133.3224 }
  ],
  energy: [
    { id: 'joule', name: 'Joule', symbol: 'J', factor: 1 },
    { id: 'kilojoule', name: 'Kilojoule', symbol: 'kJ', factor: 1000 },
    { id: 'calorie', name: 'Calorie', symbol: 'cal', factor: 4.184 },
    { id: 'kilocalorie', name: 'Kilocalorie', symbol: 'kcal', factor: 4184 },
    { id: 'watt-hour', name: 'Watt Hour', symbol: 'Wh', factor: 3600 },
    { id: 'kilowatt-hour', name: 'Kilowatt Hour', symbol: 'kWh', factor: 3600000 },
    { id: 'electronvolt', name: 'Electronvolt', symbol: 'eV', factor: 1.602176634e-19 },
    { id: 'btu', name: 'BTU', symbol: 'BTU', factor: 1055.06 }
  ],
  power: [
    { id: 'watt', name: 'Watt', symbol: 'W', factor: 1 },
    { id: 'kilowatt', name: 'Kilowatt', symbol: 'kW', factor: 1000 },
    { id: 'megawatt', name: 'Megawatt', symbol: 'MW', factor: 1000000 },
    { id: 'horsepower', name: 'Horsepower', symbol: 'hp', factor: 745.7 },
    { id: 'metric-hp', name: 'Metric Horsepower', symbol: 'PS', factor: 735.5 },
    { id: 'btu-hr', name: 'BTU/Hour', symbol: 'BTU/h', factor: 0.29307107 }
  ],
  speed: [
    { id: 'mps', name: 'Meter per Second', symbol: 'm/s', factor: 1 },
    { id: 'kmph', name: 'Kilometer per Hour', symbol: 'km/h', factor: 0.277778 },
    { id: 'mph', name: 'Mile per Hour', symbol: 'mph', factor: 0.44704 },
    { id: 'knot', name: 'Knot', symbol: 'kn', factor: 0.514444 },
    { id: 'fps', name: 'Foot per Second', symbol: 'ft/s', factor: 0.3048 },
    { id: 'mach', name: 'Mach', symbol: 'Ma', factor: 343 }
  ],
  angle: [
    { id: 'degree', name: 'Degree', symbol: '°', factor: 1 },
    { id: 'radian', name: 'Radian', symbol: 'rad', factor: 57.2957795131 },
    { id: 'gradian', name: 'Gradian', symbol: 'gon', factor: 0.9 },
    { id: 'arcminute', name: 'Arc Minute', symbol: "'", factor: 0.0166667 },
    { id: 'arcsecond', name: 'Arc Second', symbol: '"', factor: 0.000277778 },
    { id: 'revolution', name: 'Revolution', symbol: 'rev', factor: 360 }
  ],
  force: [
    { id: 'newton', name: 'Newton', symbol: 'N', factor: 1 },
    { id: 'kilonewton', name: 'Kilonewton', symbol: 'kN', factor: 1000 },
    { id: 'dyne', name: 'Dyne', symbol: 'dyn', factor: 0.00001 },
    { id: 'pound-force', name: 'Pound-force', symbol: 'lbf', factor: 4.44822 },
    { id: 'kilogram-force', name: 'Kilogram-force', symbol: 'kgf', factor: 9.80665 }
  ],
  'data-storage': [
    { id: 'bit', name: 'Bit', symbol: 'b', factor: 1 },
    { id: 'byte', name: 'Byte', symbol: 'B', factor: 8 },
    { id: 'kilobyte', name: 'Kilobyte', symbol: 'KB', factor: 8192 },
    { id: 'megabyte', name: 'Megabyte', symbol: 'MB', factor: 8388608 },
    { id: 'gigabyte', name: 'Gigabyte', symbol: 'GB', factor: 8589934592 },
    { id: 'terabyte', name: 'Terabyte', symbol: 'TB', factor: 8796093022208 },
    { id: 'petabyte', name: 'Petabyte', symbol: 'PB', factor: 9007199254740992 }
  ]
};

const ConverterPage = ({ onBack }) => {
  const { t } = useTranslation();
  const params = useParams();
  const category = params.category;
  const converterId = params.converterId;
  const unitTranslationKey = getUnitTranslationKey(converterId);
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [fromValue, setFromValue] = useState('1');
  const [toValue, setToValue] = useState('');

  // Get converter info
  const categoryInfo = converterCategories[category];
  const converterInfo = categoryInfo?.items.find(item => item.id === converterId);

  // Get units for this converter
  const units = useMemo(() => {
    // Check if we have units in our standard categories
    if (unitsByCategory[converterId]) {
      return unitsByCategory[converterId];
    }
    // Check extended units
    if (extendedUnits[converterId]) {
      return extendedUnits[converterId];
    }
    // Default fallback
    return unitsByCategory.length || [];
  }, [converterId]);

  useEffect(() => {
    if (units.length >= 2) {
      setFromUnit(units[0].id);
      setToUnit(units[1].id);
    }
  }, [converterId, units]);

  useEffect(() => {
    if (fromUnit && toUnit && fromValue) {
      const numValue = parseFloat(fromValue);
      if (!isNaN(numValue)) {
        // Use temperature conversion for temperature converter
        const categoryType = converterId === 'temperature' ? 'temperature' : converterId;
        
        const from = units.find(u => u.id === fromUnit);
        const to = units.find(u => u.id === toUnit);
        
        if (from && to) {
          if (categoryType === 'temperature') {
            const result = convertUnit(numValue, fromUnit, toUnit, 'temperature');
            setToValue(formatNumber(result));
          } else if (from.factor && to.factor) {
            const baseValue = numValue * from.factor;
            const result = baseValue / to.factor;
            setToValue(formatNumber(result));
          }
        }
      }
    }
  }, [fromValue, fromUnit, toUnit, units, converterId]);

  const handleSwap = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setFromValue(toValue);
  };

  if (!converterInfo || units.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-[#2563eb] hover:text-[#1d4ed8] mb-4"
        >
          <ArrowLeft size={18} />
          {t('common.backToAllConverters')}
        </button>
        <p className="text-gray-600">{t('common.converterNotAvailable', 'This converter is not yet available. Please try another converter.')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-[#2563eb] hover:text-[#1d4ed8]"
      >
        <ArrowLeft size={18} />
        {t('common.backToAllConverters')}
      </button>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-3">
          <h2 className="text-lg font-semibold text-white">{converterInfo.name}</h2>
        </div>

        {/* Converter Form */}
        <div className="p-6">
          <div className="grid md:grid-cols-[1fr,auto,1fr] gap-6 items-end">
            {/* From Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t('common.from')}</label>
              <input
                type="number"
                value={fromValue}
                onChange={(e) => setFromValue(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent text-lg"
                placeholder={t('common.enterValue')}
              />
              <select
                value={fromUnit}
                onChange={(e) => setFromUnit(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent"
              >
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {t(`${unitTranslationKey}.${unit.id}.name`, unit.name)} ({unit.symbol})
                  </option>
                ))}
              </select>
            </div>

            {/* Swap Button */}
            <div className="flex justify-center">
              <button
                onClick={handleSwap}
                className="p-3 rounded-full bg-[#4a7c9b] text-white hover:bg-[#3d6a86] transition-colors shadow-md"
                title={t('common.swap')}
              >
                <ArrowLeftRight size={24} />
              </button>
            </div>

            {/* To Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t('common.to')}</label>
              <input
                type="text"
                value={toValue}
                readOnly
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-lg"
                placeholder={t('common.result')}
              />
              <select
                value={toUnit}
                onChange={(e) => setToUnit(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent"
              >
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {t(`${unitTranslationKey}.${unit.id}.name`, unit.name)} ({unit.symbol})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Result Display */}
          {fromValue && toValue && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-center text-lg text-gray-700">
                <span className="font-semibold">{fromValue}</span>{' '}
                <span className="text-gray-500">{t(`${unitTranslationKey}.${fromUnit}.name`, units.find(u => u.id === fromUnit)?.name)}</span>{' = '}
                <span className="font-semibold text-[#4a7c9b] text-xl">{toValue}</span>{' '}
                <span className="text-gray-500">{t(`${unitTranslationKey}.${toUnit}.name`, units.find(u => u.id === toUnit)?.name)}</span>
              </p>
            </div>
          )}
        </div>

        {/* Quick conversions table */}
        <div className="border-t border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('common.quickReference')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left font-medium text-gray-600">From</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">To</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Result</th>
                </tr>
              </thead>
              <tbody>
                {[1, 5, 10, 25, 50, 100].map((val) => {
                  const from = units.find(u => u.id === fromUnit);
                  const to = units.find(u => u.id === toUnit);
                  let result = 0;
                  if (from && to) {
                    if (converterId === 'temperature') {
                      result = convertUnit(val, fromUnit, toUnit, 'temperature');
                    } else if (from.factor && to.factor) {
                      result = (val * from.factor) / to.factor;
                    }
                  }
                  return (
                    <tr key={val} className="border-t border-gray-100">
                      <td className="px-3 py-2">{val} {from?.symbol}</td>
                      <td className="px-3 py-2">=</td>
                      <td className="px-3 py-2 text-[#4a7c9b] font-medium">
                        {formatNumber(result)} {to?.symbol}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConverterPage;
